<template>
  <v-container class="pa-0 status-container">
    <NavHeader
      v-if="!isLoading"
      :title="$t('screens.paymentStatus.headerTitle')"
      :show-back-arrow="false"
      :subtitle="tableName"
    />
    <div v-if="isLoading" class="py-6 d-flex justify-center align-center h-100">
      <v-progress-circular indeterminate color="black" />
    </div>
    <div v-else class="h-100">
      <LayoutCard grow>
        <div
          class="d-flex flex-column justify-center align-center text-center h-100"
        >
          <!-- changing lottie src during runtime results in animations not changing this is a workaround -->
          <lottie-vue-player
            v-show="status.animation === 'success'"
            src="/assets/order-status-animations/successful-payment.json"
            style="width: 60px; height: 60px"
            autoplay
            :loop="false"
            class="mb-4"
          />
          <lottie-vue-player
            v-show="status.animation === 'loading'"
            src="/assets/order-status-animations/loading.json"
            style="width: 100px; height: 100px"
            autoplay
            loop
            class="mb-4"
          />
          <lottie-vue-player
            v-if="status.animation === 'error'"
            src="/assets/order-status-animations/failed-payment.json"
            style="width: 70px; height: 70px"
            autoplay
            :loop="false"
            class="mb-4"
          />
          <div v-show="status.showCountDown">
            <Countdown
              :end-date="new Date(countdownEndDate)"
              @ended="onEnded"
              @ending-soon="onEndingSoon"
            >
              <template #default="{ minutes, seconds }">
                <div
                  class="d-flex flex-column justify-center align-center text-center"
                >
                  <span class="countdownText mb-4">
                    {{ minutes }}:{{ seconds }}
                  </span>
                </div>
              </template>
            </Countdown>
          </div>
          <h3 class="mb-4">{{ $t(status.title) }}</h3>
          <p>
            {{ $t(status.content) }}
          </p>
          <v-fade-transition>
            <v-alert
              v-show="endingSoon"
              dense
              color="#FCECD3"
              class="text-left"
            >
              <v-row align="center">
                <v-col class="shrink">
                  <v-icon small color="black">mdi-alert</v-icon>
                </v-col>
                <v-col class="grow tp-text-body">
                  {{ $t('screens.paymentStatus.loading.longerThanExpected') }}
                </v-col>
              </v-row>
            </v-alert>
          </v-fade-transition>
        </div>
        <div class="mt-auto"></div>
        <v-fade-transition>
          <Button
            v-if="status.showReturnToOrder"
            dark
            class="mb-8"
            @click="navigateToStart"
          >
            {{ $t('screens.paymentStatus.error.cta') }}
          </Button>
        </v-fade-transition>
      </LayoutCard>
    </div>
  </v-container>
</template>

<script>
import LayoutCard from '@/components/LayoutCard.vue';
import NavHeader from '@/components/Navigation/NavHeader.vue';
import Countdown from '@/components/common/Countdown.vue';
import Button from '@/components/Button.vue';

const STATUS = {
  success: {
    title: 'screens.paymentStatus.success.title',
    content: 'screens.paymentStatus.success.content',
    animation: 'success',
    showCountDown: false,
  },
  loading: {
    title: 'screens.paymentStatus.loading.title',
    content: 'screens.paymentStatus.loading.content',
    animation: 'loading',
    showCountDown: true,
  },
  error: {
    title: 'screens.paymentStatus.error.title',
    content: 'screens.paymentStatus.error.content',
    animation: 'error',
    showCountDown: false,
    showReturnToOrder: true,
    loopAnimation: false,
  },
};

const ORIGIN = {
  PAY: 'pay',
  ORDER: 'order',
  FOOD_COURT: 'food-court',
};

// how long the loading countdown waits for the payment to resolve before
// switching to the error state; configurable via env, defaults to 2 minutes
const DEFAULT_COUNTDOWN_SECONDS = 120;
const COUNTDOWN_MS = resolveCountdownMs(
  process.env.VUE_APP_PAYMENT_STATUS_COUNTDOWN_SECONDS,
);

function resolveCountdownMs(rawSeconds) {
  const seconds = Number(rawSeconds);
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return DEFAULT_COUNTDOWN_SECONDS * 1000;
  }
  return seconds * 1000;
}

export default {
  components: {
    LayoutCard,
    NavHeader,
    Countdown,
    Button,
  },
  data() {
    return {
      isLoading: false,
      uniqueOrderNumber: null,
      failedPayment: false,
      paymentUuid: null,
      endingSoon: false,
      ended: false,
      status: STATUS.loading,
    };
  },
  computed: {
    session() {
      return this.$store.state.session;
    },
    tableName() {
      return this.$store.state.table.name;
    },
    countdownEndDate() {
      return new Date(Date.now() + COUNTDOWN_MS);
    },
  },
  async mounted() {
    this.isLoading = true;

    const uniqueOrderNumber = this.$route.query.orderNumber;
    const paymentUuid = this.$route.query.paymentUuid;

    this.uniqueOrderNumber = uniqueOrderNumber;
    this.paymentUuid = paymentUuid;

    // compatibility for BE that still sends food-court-hash
    this.foodCourtHash =
      this.$route.query['food-court-hash'] || this.$route.query.foodCourtHash;

    await this.syncLanguageFromSession();

    this.isLoading = false;

    // Poll for payment details
    await this.fetchPaymentDetails();
  },
  methods: {
    async syncLanguageFromSession() {
      const origin = this.$route.query.origin;
      const isFoodCourt = origin === ORIGIN.FOOD_COURT;

      if (isFoodCourt && this.foodCourtHash) {
        await this.$store.dispatch('fetchFoodCourtDetails', {
          hash: this.foodCourtHash,
        });
        return;
      }

      const session = this.$route.params.session;
      if (session) {
        await this.$store.dispatch('fetchSessionData', { session });
      }
    },
    async fetchPaymentDetails() {
      const isNeopay = this.$route.query['neopay-callback'] === '1';
      const hasPaymentUuid = this.$route.query['paymentUuid'] !== undefined;
      const isMollieIdeal = this.$route.query['mollie-callback'] === '1';

      if (hasPaymentUuid || isMollieIdeal) {
        await this.fetchSolidPaymentData();
        return;
      }

      if (isNeopay) {
        await this.fetchNeoPayPaymentData();
        return;
      }
    },
    async fetchPaymentData(config) {
      const { identifierKey, actionName, getIdentifier } = config;
      const identifier = getIdentifier();
      const canceled = this.$route.query.canceled === '1';
      const pending = this.$route.query.pending === '1';

      const onPaymentSuccess = (data) => {
        this.isLoading = false;
        this.paymentUuid = data.uuid;
        this.status = STATUS.success;
        this.goForward();
      };
      const onPaymentError = () => {
        this.isLoading = false;
        this.failedPayment = true;
        this.status = STATUS.error;
      };

      if (pending) {
        this.status = STATUS.loading;
        return this.$store.dispatch(actionName, {
          [identifierKey]: identifier,
          onPaymentSuccess,
          onPaymentError,
        });
      }

      if (canceled) {
        this.isLoading = false;
        this.navigateToStart();
        return;
      }

      if (identifier) {
        return this.$store.dispatch(actionName, {
          [identifierKey]: identifier,
          onPaymentSuccess,
          onPaymentError,
        });
      }
    },
    async fetchNeoPayPaymentData() {
      await this.fetchPaymentData({
        identifierKey: 'token',
        actionName: 'tryToFetchNeopayPaymentData',
        getIdentifier: () => this.$route.query.token || this.$route.query.data,
      });
    },
    async fetchSolidPaymentData() {
      await this.fetchPaymentData({
        identifierKey: 'paymentUuid',
        actionName: 'tryToFetchPaymentStatusData',
        getIdentifier: () => this.$route.query.paymentUuid,
      });
    },
    async goForward() {
      const origin = this.$route.query.origin;
      const invoiceRequested = this.$route.query.invoiceRequested;
      const payGoSuccessQuery = {
        orderNumber: this.uniqueOrderNumber,
        ...(this.paymentUuid && { paymentUuid: this.paymentUuid }),
        ...(invoiceRequested && { invoiceRequested }),
      };

      const SUCCESS_ROUTES = {
        PAY_GO_SUCCESS: 'PaymentSuccess',
        ORDER_SUCCESS: 'Menu',
        MULTI_MERCHANT_SUCCESS: 'ViewFoodCourtMultiMerchantOrder',
      };
      const REDIRECT = {
        [ORIGIN.PAY]: SUCCESS_ROUTES.PAY_GO_SUCCESS,
        [ORIGIN.ORDER]: SUCCESS_ROUTES.ORDER_SUCCESS,
        [ORIGIN.FOOD_COURT]: SUCCESS_ROUTES.MULTI_MERCHANT_SUCCESS,
      };

      const REDIRECT_PROPS = {
        [SUCCESS_ROUTES.PAY_GO_SUCCESS]: {
          params: { session: this.$route.params.session },
          query: payGoSuccessQuery,
        },
        [SUCCESS_ROUTES.ORDER_SUCCESS]: {
          params: { session: this.$route.params.session },
          query: { orderNumber: this.uniqueOrderNumber },
        },
        [SUCCESS_ROUTES.MULTI_MERCHANT_SUCCESS]: {
          params: { foodCourtHash: this.foodCourtHash },
          query: { paymentUuid: this.paymentUuid, origin: 'foodcourt' },
        },
      };
      const routerName = REDIRECT[origin] || SUCCESS_ROUTES.PAY_GO_SUCCESS;

      if (routerName === SUCCESS_ROUTES.ORDER_SUCCESS) {
        await this.$store.dispatch('openOrder', this.uniqueOrderNumber);
      }

      this.$router.push({
        name: routerName,
        replace: true,
        query: REDIRECT_PROPS[routerName].query,
        params: REDIRECT_PROPS[routerName].params,
      });
    },
    navigateToStart() {
      const origin = this.$route.query.origin;
      const REDIRECT = {
        [ORIGIN.PAY]: 'Order',
        [ORIGIN.ORDER]: 'Menu',
        [ORIGIN.FOOD_COURT]: 'FoodCourt',
      };
      const routerName = REDIRECT[origin] || REDIRECT.ORDER;

      this.$router.push({
        name: routerName,
        replace: true,
        params: this.$route.params,
        query: this.$route.query,
      });
    },
    onEndingSoon() {
      if (this.ended) return;

      if (this.status === STATUS.loading) {
        this.endingSoon = true;
      }
    },
    onEnded() {
      this.status = STATUS.error;
      this.endingSoon = false;
      this.ended = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.container {
  background-color: $color-background-gray;
  display: flex;
  flex-flow: column;
  min-height: 100svh;
  height: 100%;
}

.countdownText {
  font-size: 24px;
  font-weight: 700;
}
</style>
